// components/Footer.tsx
"use client";

import { useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { Linkedin, Youtube, Send, Globe, ChevronDown,Shield,Lock } from "lucide-react";
import { LANGUAGES } from "@/i18n/languages";
import HexLive from "@/components/HexLive";
import { InstallCTACompact } from "@/components/pwa/InstallCTACompact";

export default function Footer() {
  const t = useTranslations("footer");
  const locale = useLocale();
  const pathname = usePathname();

  const [langOpen, setLangOpen] = useState(false);
  const year = new Date().getFullYear();

  const handleLangSelect = (code: string) => {
    setLangOpen(false);
    window.location.href = `/${code}${pathname === "/" ? "" : pathname}`;
  };

  const currentLang = LANGUAGES.find((l) => l.code === locale);

  const socials = [
    { icon: Linkedin, href: "https://linkedin.com",   label: "LinkedIn" },
    { icon: Youtube,  href: "https://youtube.com",    label: "YouTube"  },
    { icon: Send,     href: "https://t.me/salaria",   label: "Telegram" },
  ];

 return (
    <footer className="relative border-t border-base bg-base text-fore">
      <HexLive className="hidden md:block absolute left-[4%] top-4 w-10 h-10" mode="spark" baseOpacity={0.14} intervalSec={30} />
      <HexLive className="hidden md:block absolute left-[12%] top-16 w-6 h-6" mode="pulse" baseOpacity={0.10} intervalSec={48} />
      <HexLive className="hidden md:block absolute left-[22%] bottom-6 w-16 h-16" mode="pulse" baseOpacity={0.08} intervalSec={52} />
      <HexLive className="hidden md:block absolute left-[35%] top-8 w-8 h-8" mode="spark" baseOpacity={0.12} intervalSec={38} />
      <HexLive className="hidden md:block absolute left-[48%] bottom-10 w-5 h-5" mode="pulse" baseOpacity={0.10} intervalSec={44} />
      <HexLive className="hidden md:block absolute right-[38%] top-6 w-12 h-12" mode="spark" baseOpacity={0.10} intervalSec={35} />
      <HexLive className="hidden md:block absolute right-[25%] bottom-4 w-9 h-9" mode="pulse" baseOpacity={0.09} intervalSec={50} />
      <HexLive className="hidden md:block absolute right-[15%] top-12 w-24 h-24" mode="pulse" baseOpacity={0.07} intervalSec={55} />
      <HexLive className="hidden md:block absolute right-[6%] bottom-8 w-7 h-7" mode="spark" baseOpacity={0.13} intervalSec={33} />
      <HexLive className="hidden md:block absolute right-[10%] top-2 w-5 h-5" mode="pulse" baseOpacity={0.10} intervalSec={46} />
      <div className="max-w-7xl mx-auto px-6 py-10 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">

          {/* Brand */}
          <div className="md:col-span-4">
            <Link href="/" className="inline-flex items-center gap-2 mb-3">
              <img src="/layvers-logo.png" alt="Layvers" className="h-6 w-auto" />
              <span className="text-base font-bold tracking-tight" style={{ color: "rgb(var(--brand-primary))" }}>
                Layvers
              </span>
              
            </Link>
            <p className="text-xs leading-relaxed max-w-xs text-mute">
              {t("tagline")}
            </p>
            
            </div>

          {/* Nav links */}
          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-fore">
              {t("nav.title")}
            </h3>
            <ul className="space-y-2 text-xs">
              <li><Link href="/pricing" className="transition-colors"
onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
onMouseLeave={(e) => (e.currentTarget.style.color = "")}>{t("nav.pricing")}</Link></li>
              <li><Link href="/about" className="transition-colors"
onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
onMouseLeave={(e) => (e.currentTarget.style.color = "")}>{t("nav.about")}</Link></li>
              <li><Link href="/pricing#faq" className="transition-colors"
onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
onMouseLeave={(e) => (e.currentTarget.style.color = "")}>{t("nav.faq")}</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-fore">
              {t("legal.title")}
            </h3>
            <ul className="space-y-2 text-xs">
              <li><Link href="/terms" className="transition-colors"
onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
onMouseLeave={(e) => (e.currentTarget.style.color = "")}>{t("legal.terms")}</Link></li>
              <li><Link href="/privacy" className="transition-colors"
onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
onMouseLeave={(e) => (e.currentTarget.style.color = "")}>{t("legal.privacy")}</Link></li>
              <li><Link href="/cookies" className="transition-colors"
onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
onMouseLeave={(e) => (e.currentTarget.style.color = "")}>{t("legal.cookies")}</Link></li>
              <li>
                <button
                  onClick={async () => {
                    try {
                      const sid = localStorage.getItem("salaria_session_id") || crypto.randomUUID();
                      await fetch("/api/consent/log", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          sessionId: sid,
                          domain: window.location.hostname,
                          consent: { necessary: true, functional: false, analytics: false },
                          source: "withdraw",
                        }),
                      });
                    } catch {}

                    localStorage.removeItem("salaria_cookie_consent");
                    if (typeof window !== "undefined" && (window as any).Cookiebot) {
                      (window as any).Cookiebot.renew();
                    }
                    window.location.reload();
                  }}
                  className="transition-colors text-start"
                  onMouseEnter={(e) => (e.currentTarget.style.color = "rgb(var(--accent))")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                >
                  {t("legal.cookieSettings")}
                </button>
              </li>
            </ul>
          </div>

          {/* Socials + Lang */}
          {/* Socials */}
         {/* <div className="md:col-span-4 flex flex-col gap-5">
            
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-fore">
                {t("social.title")}
              </h3>
              <div className="flex items-center gap-3">
                {socials.map((s) => (
                 <a 
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="p-2 rounded-lg border border-base transition-all"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "rgb(var(--accent))";
                          e.currentTarget.style.backgroundColor = "rgb(var(--accent) / 0.1)";
                          e.currentTarget.style.borderColor = "rgb(var(--accent) / 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "";
                          e.currentTarget.style.backgroundColor = "";
                          e.currentTarget.style.borderColor = "";
                        }}
                    
                  >
                    <s.icon className="w-3.5 h-3.5" />
                  </a>
                ))}
              </div>
            </div>*/}

            {/* Language switcher */}
          {/*  <div className="relative">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-fore">
                {t("language")}
              </h3>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-base bg-card hover:border-accent-md transition-all"
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{currentLang?.native ?? locale}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>

              {langOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                  <div className="absolute bottom-full mb-2 start-0 w-56 rounded-xl border border-base shadow-card z-20 overflow-hidden bg-base">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => handleLangSelect(l.code)}
                        className={`w-full text-start px-4 py-2.5 text-sm transition-colors
                          ${locale === l.code
                            ? "bg-accent-soft text-accent font-semibold"
                            : "hover:bg-panel text-fore"
                          }`}
                      >
                        <span>{l.native}</span>
                        <span className="ms-2 text-xs text-mute">{l.english}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div> */}
          <div className="md:col-span-4">
              <InstallCTACompact />
          </div>
      </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-5 text-xs text-fore/70 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>{t("copyright", { year })}</span>
            <div className="flex items-center gap-4 text-[10px] text-fore/60">
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {t("soc2")}
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t("encrypted")}
              </span>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-base bg-card hover:border-accent-md transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{currentLang?.native ?? locale}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                <div className="absolute bottom-full mb-2 end-0 w-56 rounded-xl border border-base shadow-card z-20 overflow-hidden bg-base">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => handleLangSelect(l.code)}
                      className={`w-full text-start px-4 py-2.5 text-sm transition-colors ${locale === l.code ? "bg-accent-soft text-accent font-semibold" : "hover:bg-panel text-fore"}`}
                    >
                      <span>{l.native}</span>
                      <span className="ms-2 text-xs text-mute">{l.english}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}